import { FC } from 'react'

import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { formatPlacementAttribute } from '../../../containers/EmbroideryContainer/utils/formatPlacementAttribute'
import {
  EmbroideryLine,
  EmbroideryTypeSneakPeeksSummary,
} from '../../../containers/EmbroideryContainer/utils/summarizeEmbroideryGroup'
import { formatPrettyCurrency } from '../../../lib/utils'
import { DTCLogoSneakPeek } from '../EmbroiderySneakPeek/DTCLogoSneakpeek'
import { IconSneakPeek } from '../EmbroiderySneakPeek/IconSneakPeek'
import { TextSneakPeek } from '../EmbroiderySneakPeek/TextSneakPeek'

import { EmbroiderySectionSubtotal, EmbroideryTitle } from './styles'

interface EmbroideryTypeSneakPeeksProps {
  section: EmbroideryTypeSneakPeeksSummary
  hasLogoSetup: boolean
  showPrices: boolean
  // TODO: post-launch of emb multiplacement, we want to allow for individual embroidery fee removal
  removeLine?: (line: EmbroideryLine) => void
}

/**
 * v2.0 display for embroidery sneak peeks within an `EmbroideryGroupContent`.
 *
 * Supports D2C embroidery only (until B2B is on v2.0).
 */
export const EmbroideryTypeSneakPeeks: FC<EmbroideryTypeSneakPeeksProps> = ({
  section,
  hasLogoSetup,
  showPrices,
}) => {
  const { currency, locale } = useLocalization()
  const {
    embroidery: {
      cart: { textTitle, iconTitle, logoTitle },
    },
  } = useTranslation()

  const prettySubtotal = formatPrettyCurrency({
    currency,
    locale,
    unit: 'cents',
    value: section.subtotal.effectivePrice,
  })

  const sectionSubtitle = () => {
    const title =
      section.type === 'text' ? textTitle : section.type === 'icon' ? iconTitle : logoTitle

    return (
      <li role='presentation'>
        <EmbroideryTitle>{title}</EmbroideryTitle>
        {showPrices && <EmbroiderySectionSubtotal> - {prettySubtotal}</EmbroiderySectionSubtotal>}
      </li>
    )
  }

  return (
    <>
      {sectionSubtitle()}
      {section.type === 'text' && (
        <>
          {section.lines.map(({ properties: props, key }) => (
            <TextSneakPeek
              key={key}
              role='listitem'
              backgroundColor={props._scrub_color}
              textColorCssValue={props._text_thread_color_value}
              textColorDisplayName={props.text_color}
              textFirstLine={props.text_first_line}
              textFontCssFamily={props._text_font_family_value}
              textFontDisplayName={props.text_font}
              textPlacement={formatPlacementAttribute(
                props.text_placement,
                props._embroidery_version
              )}
              textSecondLine={props.text_second_line}
            />
          ))}
        </>
      )}
      {section.type === 'icon' && (
        <>
          {section.lines.map(({ properties: props, key }) => (
            <IconSneakPeek
              key={key}
              role='listitem'
              backgroundColor={props._scrub_color}
              iconImageSource={props._icon_image_source}
              iconInitials={undefined}
              iconInitialsColorCss={undefined}
              iconInitialsColorDisplayName={undefined}
              iconDesignName={props.icon_design}
              iconPlacement={formatPlacementAttribute(
                props.icon_placement,
                props._embroidery_version
              )}
            />
          ))}
        </>
      )}
      {section.type === 'logo' && (
        <>
          {section.lines.map(({ properties: props, key }) => (
            <DTCLogoSneakPeek
              key={key}
              role='listitem'
              backgroundColor={props._scrub_color}
              imgSrc={props._logo_preview}
              logoName={props.logo_name}
              logoPlacement={formatPlacementAttribute(
                props.logo_placement,
                props._embroidery_version
              )}
              logoColor={props.logo_color_option}
              logoNote={props.logo_note}
              hasLogoSetup={hasLogoSetup}
            />
          ))}
        </>
      )}
    </>
  )
}
